import { css } from '@linaria/core';
import { HTMLAttributes, forwardRef, useMemo } from 'react';
import theme from '../theme';

const baseClass = css`
    display: block;
    border: unset;
    text-align: unset;

    background-color: ${theme.color.white.main};
    border-radius: 5px;
    padding: 10px;
    margin: 5px 0;
    cursor: pointer;
`;

const disabledClass = css`
    opacity: 0.5;
`;

const hoverClass = css`
    &:hover {
        box-shadow: ${theme.boxShadow.medium};
    }
`;

const hoveredClass = css`
    box-shadow: ${theme.boxShadow.medium};
`;

const selectedClass = css`
    border: solid 1px ${theme.color.blue.primary};
    box-shadow: ${theme.boxShadow.main};
`;

interface BlockProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'className' | 'onClick' | 'type'> {
    disabled?: boolean;
    hovered?: boolean;
    onSelect?: () => void;
    onUnselect?: () => void;
    selected?: boolean;
}

const Block = forwardRef<HTMLButtonElement, BlockProps>(
    ({ children, disabled = false, hovered, onSelect, onUnselect, selected = false, ...rest }, ref) => {
        const classes = [baseClass];

        if (disabled) {
            classes.push(disabledClass);
        }

        if (selected) {
            classes.push(selectedClass);
        } else {
            if (hovered === undefined) {
                classes.push(hoverClass);
            }

            if (hovered === true) {
                classes.push(hoveredClass);
            }
        }

        const onClick = useMemo(() => {
            if (!onSelect && !onUnselect) {
                return undefined;
            }

            return () => {
                if (selected) {
                    onUnselect?.();
                } else {
                    onSelect?.();
                }
            };
        }, [onSelect, onUnselect, selected]);

        return (
            <button {...rest} className={classes.join(' ')} onClick={onClick} ref={ref} type="button">
                {children}
            </button>
        );
    },
);

export default Block;
